import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #stockOrderObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeStockOrdering();
    this.#rankOutOfStockItems();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#stockOrderObserver?.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #shouldRankOutOfStockItems() {
    return !this.querySelector('[data-filter-param-name*="availability"], [data-filter-param-name*="stock"]');
  }

  #observeStockOrdering() {
    const { grid } = this.refs;

    this.#stockOrderObserver?.disconnect();

    if (!grid || !this.#shouldRankOutOfStockItems()) return;

    this.#stockOrderObserver = new MutationObserver((mutations) => {
      const hasChildListChange = mutations.some((mutation) => mutation.type === 'childList');

      if (hasChildListChange) {
        this.#rankOutOfStockItems();
      }
    });

    this.#stockOrderObserver.observe(grid, { childList: true });
  }

  #rankOutOfStockItems() {
    const { grid } = this.refs;

    if (!grid || !this.#shouldRankOutOfStockItems()) return;

    const children = Array.from(grid.children);
    const cards = children.filter(
      (child) => child instanceof HTMLElement && child.getAttribute('ref') === 'cards[]'
    );

    if (cards.length < 2) return;

    const inStockCards = [];
    const outOfStockCards = [];

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;

      if (card.dataset.productAvailable === 'false') {
        outOfStockCards.push(card);
      } else {
        inStockCards.push(card);
      }
    }

    if (outOfStockCards.length === 0) return;

    const reorderedCards = [...inStockCards, ...outOfStockCards];
    const reorderedChildren = [];
    let reorderedCardIndex = 0;

    for (const child of children) {
      if (child instanceof HTMLElement && child.getAttribute('ref') === 'cards[]') {
        reorderedChildren.push(reorderedCards[reorderedCardIndex]);
        reorderedCardIndex += 1;
      } else {
        reorderedChildren.push(child);
      }
    }

    const orderChanged = reorderedChildren.some((child, index) => child !== children[index]);

    if (orderChanged) {
      grid.replaceChildren(...reorderedChildren);
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
